import { useState, type KeyboardEvent } from 'react';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Comma/Enter-separated email chips input. Commits on Enter, comma, or blur.
 * Backspace on empty input removes the last chip. Duplicates and invalid
 * addresses are silently rejected.
 */
export function EmailChipsInput({ value, onChange, placeholder, disabled }: Props) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function commit() {
    const raw = draft.trim().replace(/,$/, '').trim();
    if (!raw) { setDraft(''); return; }
    if (!EMAIL_RE.test(raw)) {
      setError(`Not a valid email: ${raw}`);
      return;
    }
    const lower = raw.toLowerCase();
    if (value.includes(lower)) {
      setDraft('');
      return;
    }
    onChange([...value, lower]);
    setDraft('');
    setError(null);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (draft.trim()) {
        e.preventDefault();
        commit();
      }
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function remove(addr: string) {
    onChange(value.filter((v) => v !== addr));
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1 px-2 py-1.5 border border-slate-300 rounded-md min-h-[34px] bg-white">
        {value.map((addr) => (
          <span
            key={addr}
            className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs rounded px-1.5 py-0.5"
          >
            {addr}
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(addr)}
                className="text-slate-500 hover:text-slate-700"
                aria-label={`Remove ${addr}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={onKeyDown}
          onBlur={() => { if (draft.trim()) commit(); }}
          placeholder={value.length === 0 ? (placeholder ?? 'name@example.com') : ''}
          disabled={disabled}
          className="flex-1 min-w-[120px] text-sm outline-none bg-transparent"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
